<template>
	<div class="col-span-full p-6 h-full flex flex-col pb-10">

		<div>
			<div class="flex justify-center items-center text-center">
				<div class="max-w-md">
					<h3 class="text-2xl font-semibold mb-2">
						{{ $t('Ready to be inspired?') }}
					</h3>
					<div>
						{{ $t('Discover and make use of lesson plans shared by your colleagues, other teachers, authors and publishers.') }}
					</div>
				</div>
			</div>

			<div class="flex flex-col gap-y-10">

				<LessonPlanDiscoverSection
					v-if="recentlyViewedAuthors.length > 0"
					:title="$t('Recently viewed')"
					:authorDataList="recentlyViewedAuthors"
					:max-items-to-show="4"
					:show-lesson-plan-counter="false"
					border-color-class="border-[#fbbd23]"
					card-size="sm"
					@selectAuthor="selectAuthor"
				/>

				<LessonPlanDiscoverSection
					:title="$t('Colleague', 2)"
					:showLoading="isLoading"
					:authorDataList="colleaguesAuthorData"
					:max-items-to-show="9"
					cardSize="md"
					borderColorClass="border-accent-focus"
					@selectAuthor="selectAuthor"
					@selectGroup="selectAuthorGroup('colleagues')"
				>
					<template #emptyState>
						<p class="text-sm py-4">
							{{ $t('No colleagues sharing their lesson plans yet.') }}
						</p>
						<p class="setting-explanation pb-4">
							{{ $t('Learn how to share with colleagues.') }}
							<button class="btn-help ml-1" style="display: inline-flex; vertical-align: middle;" @click="showShareInfo = true"></button>
						</p>
					</template>
				</LessonPlanDiscoverSection>

				<LessonPlanDiscoverSection
					:title="$t('Publisher', 2)"
					:showLoading="isLoading"
					:authorDataList="publishersAuthorData"
					:max-items-to-show="15"
					cardSize="md"
					borderColorClass="border-accent2-focus"
					@selectAuthor="selectAuthor"
					@selectGroup="selectAuthorGroup('publishers')"
				/>

			</div>
		</div>

		<KBView v-if="showShareInfo" :url="{
			en: '',
			nl: 'https://handsomely-cicada-2cb.notion.site/ebd//34a0964c114280a4abb6c7ac16cae054',
		}" @requestClose="showShareInfo = false" />
	</div>
</template>


<script lang="ts">
import { useLessonPlanCollectionsFromColleaguesStore, usePublicLessonPlanCollectionsStore, AuthorData } from '@/stores/DiscoverCollections.store';
import { User } from '@/models/User.model';
import SmaSyLocalStorage from '@/utils/SmaSyLocalStorage.util';
import LessonPlanDiscoverSection from '@/components/LessonPlanDiscoverSection.vue';
import KBView from '@/components/KBView.vue';

const RECENTLY_VIEWED_KEY = 'discover_recently_viewed_authors';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;


export default {

	name: 'LessonPlanDiscoverHome',
	emits: ['selectAuthor', 'selectAuthorGroup'],

	components: {
		LessonPlanDiscoverSection,
		KBView,
	},

	data() {
		return {
			showShareInfo: false,
		};
	},

	computed: {
		collectionsFromColleaguesStore() {
			return useLessonPlanCollectionsFromColleaguesStore();
		},

		publicCollectionsStore() {
			return usePublicLessonPlanCollectionsStore();
		},

		isLoading() {
			return this.collectionsFromColleaguesStore.loading || this.publicCollectionsStore.loading;
		},

		colleaguesAuthorData() {
			return this.collectionsFromColleaguesStore.groupedByAuthor;
		},

		publishersAuthorData() {
			return this.publicCollectionsStore.groupedByAuthor;
		},

		recentlyViewedAuthors(): AuthorData[] {
			const entries: { userId: string; section: string; viewedAt: number }[] = SmaSyLocalStorage.getCurrentUserItem(RECENTLY_VIEWED_KEY) || [];
			const cutoff = Date.now() - THIRTY_DAYS_MS;
			const recentIds = new Map(
				entries
					.filter((e) => e.viewedAt >= cutoff)
					.map((e) => [`${e.section}:${e.userId}`, e.viewedAt])
			);
			if (recentIds.size === 0) return [];

			const colleaguesByAuthorId = new Map<string, AuthorData>(
				this.collectionsFromColleaguesStore.groupedByAuthor.map((authorData: AuthorData) => [authorData.author.id, authorData])
			);
			const publishersByAuthorId = new Map<string, AuthorData>(
				this.publicCollectionsStore.groupedByAuthor.map((authorData: AuthorData) => [authorData.author.id, authorData])
			);
			const seen = new Set<string>();
			const matched: { authorData: AuthorData; viewedAt: number }[] = [];
			for (const entry of entries) {
				if (entry.viewedAt < cutoff) continue;

				const key = `${entry.section}:${entry.userId}`;
				if (seen.has(key)) continue;

				const authorData: AuthorData | null = entry.section === 'colleagues'
					? colleaguesByAuthorId.get(entry.userId) ?? null
					: entry.section === 'public'
						? publishersByAuthorId.get(entry.userId) ?? null
						: null;

				if (authorData && recentIds.has(key)) {
					seen.add(key);
					matched.push({ authorData, viewedAt: recentIds.get(key)! });
				}
			}
			return matched
				.sort((a, b) => b.viewedAt - a.viewedAt)
				.slice(0, 4)
				.map((m) => m.authorData);
		},
	},

	methods: {

		selectAuthor(author: User, section: string) {
			this.$emit('selectAuthor', author.id, section);
		},

		selectAuthorGroup(groupId: string) {
			this.$emit('selectAuthorGroup', groupId);
		},

	}
};

</script>
